/* Phase F — Language context + translation hook.
 * Strategy:
 * - LanguageProvider reads/writes the preferred language to localStorage.
 * - useT() returns a `t(text, opts?)` function. It synchronously returns the
 *   English text on first render (so the page never blank-flashes), then
 *   asynchronously fetches a batched translation and re-renders.
 * - All requested strings are pooled in a 50ms debounce window so one page
 *   render becomes ONE network call.
 * - Translations are cached client-side per session and server-side forever.
 * - English passthrough is a no-op (no network call).
 *
 * Safety: strings that should NEVER be translated (Stripe button labels, etc.)
 * are simply rendered without wrapping them in t() — opt-in by design.
 */
import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'nhf_lang';
const AUTODETECT_FLAG = 'nhf_lang_autodetected';
const DEFAULT_LANG = 'en';
const DICT_STORAGE_PREFIX = 'nhf_dict_';
const DICT_MAX_ENTRIES_PER_LANG = 4000; // hard cap to keep localStorage healthy

export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', native: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Spanish', native: 'Español', flag: '🇪🇸' },
  { code: 'ht', name: 'Haitian Creole', native: 'Kreyòl Ayisyen', flag: '🇭🇹' },
  { code: 'fr', name: 'French', native: 'Français', flag: '🇫🇷' },
];

const SUPPORTED_CODES = new Set(SUPPORTED_LANGUAGES.map((l) => l.code));

/* Routes where we deliberately DO NOT mirror lang into the URL (admin tools
 * don't need shareable-link translation and would just get noisy URLs). */
const SKIP_URL_SYNC_PREFIXES = ['/admin', '/auth'];

const shouldSyncToUrl = (pathname = '') =>
  !SKIP_URL_SYNC_PREFIXES.some((p) => pathname.startsWith(p));

/* Mirror the active language into the current URL as ?lang=xx (or strip it for
 * English). Uses replaceState so we don't pollute the browser history stack. */
const syncLangToUrl = (code) => {
  if (typeof window === 'undefined' || !window.location) return;
  if (!shouldSyncToUrl(window.location.pathname)) return;
  try {
    const url = new URL(window.location.href);
    const current = url.searchParams.get('lang');
    if (code === DEFAULT_LANG) {
      if (current === null) return;
      url.searchParams.delete('lang');
    } else {
      if (current === code) return;
      url.searchParams.set('lang', code);
    }
    const next = url.pathname + (url.search ? url.search : '') + (url.hash || '');
    window.history.replaceState(window.history.state, '', next);
  } catch { /* ignore — replaceState may fail in old browsers */ }
};

/* Read & validate `?lang=xx` from the current URL. Returns the code or null. */
const readLangFromUrl = () => {
  if (typeof window === 'undefined' || !window.location) return null;
  try {
    const raw = new URLSearchParams(window.location.search).get('lang');
    if (!raw) return null;
    const code = raw.toLowerCase();
    return SUPPORTED_CODES.has(code) ? code : null;
  } catch { return null; }
};

/* Detect the visitor's preferred language from the browser ONLY on first visit
 * (i.e. when no language has been saved to localStorage). Walks navigator.languages
 * in priority order, matches the BCP-47 prefix against our supported set, and falls
 * back to English. Safe for SSR — guards against window being undefined. */
const detectInitialLanguage = () => {
  try {
    // 1. URL ?lang=xx wins — shared links must override everything else so the
    //    recipient sees the language the sharer intended.
    const fromUrl = readLangFromUrl();
    if (fromUrl) {
      try {
        localStorage.setItem(STORAGE_KEY, fromUrl);
        localStorage.removeItem(AUTODETECT_FLAG);
      } catch { /* ignore */ }
      return fromUrl;
    }

    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && SUPPORTED_CODES.has(saved)) return saved;

    if (typeof navigator === 'undefined') return DEFAULT_LANG;
    const candidates = (navigator.languages && navigator.languages.length)
      ? navigator.languages
      : [navigator.language || ''];
    for (const raw of candidates) {
      const prefix = (raw || '').toLowerCase().split('-')[0];
      if (SUPPORTED_CODES.has(prefix)) {
        // Save with the auto-detected flag so we know the choice wasn't manual
        localStorage.setItem(STORAGE_KEY, prefix);
        localStorage.setItem(AUTODETECT_FLAG, '1');
        return prefix;
      }
    }
    // Not supported — persist English so we don't re-detect next time
    localStorage.setItem(STORAGE_KEY, DEFAULT_LANG);
    return DEFAULT_LANG;
  } catch {
    return DEFAULT_LANG;
  }
};

const LanguageContext = createContext({
  lang: DEFAULT_LANG,
  setLang: () => {},
  // dictionaries: { [lang]: { [english]: translated } }
  dictionaries: {},
  // pendingRequests: ref to dedupe in-flight requests
});

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(detectInitialLanguage);
  // Dictionary cache, per language: { [english string]: translated string }
  // Hydrated from localStorage on mount so reloads in non-EN are instant.
  const [dictionaries, setDictionaries] = useState(() => {
    const out = {};
    try {
      for (const code of ['es', 'ht', 'fr']) {
        const raw = localStorage.getItem(DICT_STORAGE_PREFIX + code);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && typeof parsed === 'object') out[code] = parsed;
        }
      }
    } catch { /* ignore — localStorage may be disabled */ }
    return out;
  });
  const [isTranslating, setIsTranslating] = useState(false);
  const pendingRef = useRef({}); // { [lang]: Set<string> } — pooled requests
  const inflightRef = useRef({}); // { [lang]: Promise } — current flight
  const timerRef = useRef(null);
  const subscribersRef = useRef(new Set()); // force re-renders on dictionary update
  const inflightCountRef = useRef(0);

  // Persist a language's dictionary to localStorage after each update.
  const persistDictionary = (code, dict) => {
    if (code === 'en') return;
    try {
      // Trim if oversized (FIFO via object key order, which is insertion order)
      let toStore = dict;
      const keys = Object.keys(dict);
      if (keys.length > DICT_MAX_ENTRIES_PER_LANG) {
        toStore = {};
        const recent = keys.slice(-DICT_MAX_ENTRIES_PER_LANG);
        recent.forEach((k) => { toStore[k] = dict[k]; });
      }
      localStorage.setItem(DICT_STORAGE_PREFIX + code, JSON.stringify(toStore));
    } catch { /* quota exceeded — skip silently */ }
  };

  const trackInflight = (delta) => {
    inflightCountRef.current = Math.max(0, inflightCountRef.current + delta);
    setIsTranslating(inflightCountRef.current > 0);
  };

  const setLang = (next) => {
    if (next === lang) return;
    setLangState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
      // Manual choice overrides auto-detect for all future visits.
      localStorage.removeItem(AUTODETECT_FLAG);
    } catch { /* ignore */ }
    // Mirror selection into URL so the page is now copy-shareable in this language.
    syncLangToUrl(next);
    // Notify subscribers so wrapped texts re-translate
    subscribersRef.current.forEach((cb) => cb());
  };

  // Flush pooled requests for the active language.
  const flush = useCallback(async () => {
    timerRef.current = null;
    const target = lang;
    if (target === 'en') return;
    const pool = pendingRef.current[target];
    if (!pool || pool.size === 0) return;
    const batch = Array.from(pool);
    pendingRef.current[target] = new Set();

    try {
      const res = await axios.post(`${BACKEND_URL}/api/translate`,
        { texts: batch, target_lang: target });
      const map = res.data?.translations || {};
      setDictionaries((prev) => {
        const merged = { ...(prev[target] || {}), ...map };
        persistDictionary(target, merged);
        return { ...prev, [target]: merged };
      });
      // Notify subscribers so the page re-renders with the new translations
      subscribersRef.current.forEach((cb) => cb());
    } catch (e) {
      // On failure, silently fall back to English. The UI already shows English.
    }
  }, [lang]);

  const enqueue = useCallback((text) => {
    if (lang === 'en' || !text) return;
    if (!pendingRef.current[lang]) pendingRef.current[lang] = new Set();
    const dict = dictionaries[lang] || {};
    if (dict[text] !== undefined) return;  // already translated
    pendingRef.current[lang].add(text);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flush, 50);
  }, [lang, dictionaries, flush]);

  // Pre-warm static labels whenever lang changes (so nav and footer translate fast)
  useEffect(() => {
    if (lang === 'en') return;
    // Static labels are pre-warmed via t() calls in the layout components themselves.
    // No-op here — they'll get enqueued naturally on render.
    return () => {};
  }, [lang]);

  const value = {
    lang,
    setLang,
    dictionaries,
    enqueue,
    isTranslating,
    trackInflight,
    persistDictionary,
    setDictionaries,
    subscribe: (cb) => { subscribersRef.current.add(cb); return () => subscribersRef.current.delete(cb); },
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => useContext(LanguageContext);

/* useT — returns a t() function for translating strings inline. */
export const useT = () => {
  const ctx = useContext(LanguageContext);
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    if (!ctx.subscribe) return undefined;
    return ctx.subscribe(() => forceUpdate((n) => n + 1));
  }, [ctx]);

  return (text) => {
    if (!text || typeof text !== 'string') return text;
    if (ctx.lang === 'en') return text;
    const dict = ctx.dictionaries[ctx.lang] || {};
    if (dict[text] !== undefined) return dict[text];
    // Not yet translated — request it and render English in the meantime
    ctx.enqueue(text);
    return text;
  };
};

/* <T> — drop-in wrapper for static JSX text:
 *   <T>Donate Now</T>
 *   <T>Welcome to NHF</T>
 */
export const T = ({ children }) => {
  const t = useT();
  if (typeof children !== 'string') return children;
  return t(children);
};

export default LanguageContext;
